import React, { useEffect, useState } from "react";
import { CreditCard, Loader2, TrendingUp, CalendarDays } from "lucide-react";
import client from "@/lib/api";

const STATUS = {
  active: "text-emerald-700 bg-emerald-100",
  trialing: "text-blue-700 bg-blue-100",
  canceled: "text-rose-700 bg-rose-100",
};

const eur = (n) => `€${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ label, value, sub }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="text-sm text-slate-500">{label}</div>
    <div className="font-heading text-3xl font-extrabold mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

export default function AdminSubscriptions() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    client.get("/admin/subscriptions").then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-subscriptions">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><CreditCard size={24} className="text-sky-600" /> Subscriptions</h1>
        <p className="text-slate-500">Stripe & manually-granted plans</p>
      </div>

      {loading && <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" /></div>}

      {!loading && d && (
        <>
          {/* Revenue widget */}
          <div className="rounded-3xl p-6 mb-6 mel-gradient relative overflow-hidden shadow-[0_20px_50px_-20px_rgba(124,58,237,0.7)]" data-testid="revenue-widget">
            <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-white/15 blur-2xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 text-white/75 text-xs font-bold tracking-[0.18em] uppercase mb-2"><TrendingUp size={14} /> Monthly recurring (MRR)</div>
                <div className="font-heading text-4xl font-extrabold text-white" data-testid="revenue-mrr">{eur(d.summary.estimated_mrr)}</div>
                <div className="text-sm text-white/70 mt-1">{d.summary.paying} paying · {d.summary.monthly_subs} monthly · {d.summary.yearly_subs} yearly</div>
              </div>
              <div className="md:border-l md:border-white/20 md:pl-6">
                <div className="flex items-center gap-2 text-white/75 text-xs font-bold tracking-[0.18em] uppercase mb-2"><CalendarDays size={14} /> Annual run-rate (ARR)</div>
                <div className="font-heading text-4xl font-extrabold text-white" data-testid="revenue-arr">{eur(d.summary.estimated_arr)}</div>
                <div className="text-sm text-white/70 mt-1">Projected 12-month revenue at current MRR</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat label="Active" value={d.summary.active} />
            <Stat label="Trialing" value={d.summary.trialing} />
            <Stat label="Canceled" value={d.summary.canceled} />
            <Stat label="Manual grants" value={d.summary.manual} />
          </div>

          {d.items.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="subs-empty">
              <p className="font-heading font-bold text-lg">No subscriptions yet</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Plan</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Renews / ends</th>
                      <th className="px-4 py-3 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.items.map((r, i) => (
                      <tr key={i} data-testid={`subs-row-${i}`}>
                        <td className="px-4 py-3"><div className="font-medium text-slate-700">{r.name || "—"}</div><div className="text-xs text-slate-400">{r.email}</div></td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded-lg text-xs font-bold ${STATUS[r.status] || "text-slate-600 bg-slate-100"}`}>{r.status}{r.cancel_at_period_end ? " (ending)" : ""}</span></td>
                        <td className="px-4 py-3 text-slate-500">{r.plan || "—"} {r.interval ? `/${r.interval}` : ""}</td>
                        <td className="px-4 py-3 text-slate-500">{r.amount ? `${(r.currency || "EUR").toUpperCase()} ${r.amount}` : "—"}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.current_period_end ? new Date(r.current_period_end).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 text-slate-500">{r.manual ? "Manual" : "Stripe"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
